export const VOICE_NOTE_PRONUNCIATION_TOPIC = "chickytutor.voice-note.pronunciation.v1";
export const VOICE_NOTE_PRONUNCIATION_EVENT_TYPE = "voice_note_pronunciation_analysis";
export const VOICE_NOTE_PRONUNCIATION_PROTOCOL_VERSION = 1;

export type VoiceNotePronunciationProvider = "azure" | "lingolix";

export interface AzurePronunciationWord {
  text: string;
  accuracy: number | null;
  errorType: string;
}

export interface AzurePronunciationAnalysis {
  kind: "azure";
  scores: Partial<Record<"pronunciation" | "accuracy" | "fluency" | "prosody", number>>;
  text: string;
  words: AzurePronunciationWord[];
  segmentCount: number;
  summaryIsApproximate: boolean;
  wordsTruncated: boolean;
}

export interface LingolixPronunciationSyllable {
  text: string;
  expectedIpa: string;
  detectedIpa: string;
  accuracy: number | null;
  completeness: number | null;
  pitch: string;
  durationMs: number | null;
  isMissing: boolean;
  isExtra: boolean;
}

export interface LingolixPronunciationWord {
  text: string;
  accuracy: number | null;
  completeness: number | null;
  syllables: LingolixPronunciationSyllable[];
}

export interface LingolixPronunciationAnalysis {
  kind: "lingolix";
  accuracy: number | null;
  completeness: number | null;
  speakingRate: number | null;
  text: string;
  words: LingolixPronunciationWord[];
  wordsTruncated: boolean;
}

export type VoiceNotePronunciationAnalysis =
  | AzurePronunciationAnalysis
  | LingolixPronunciationAnalysis;

interface VoiceNotePronunciationBase {
  voiceNoteId: string;
  provider: VoiceNotePronunciationProvider;
  languageCode?: string;
}

export type VoiceNotePronunciationState =
  | (VoiceNotePronunciationBase & { status: "pending" })
  | (VoiceNotePronunciationBase & {
      status: "unavailable";
      error?: string;
    })
  | (VoiceNotePronunciationBase & {
      status: "complete";
      analysis: VoiceNotePronunciationAnalysis;
    });

function isProvider(value: unknown): value is VoiceNotePronunciationProvider {
  return value === "azure" || value === "lingolix";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isAzureAnalysis(value: unknown): value is AzurePronunciationAnalysis {
  return (
    isRecord(value) &&
    value.kind === "azure" &&
    isRecord(value.scores) &&
    Array.isArray(value.words)
  );
}

function isLingolixAnalysis(value: unknown): value is LingolixPronunciationAnalysis {
  return (
    isRecord(value) &&
    value.kind === "lingolix" &&
    Array.isArray(value.words) &&
    value.words.every((word) => isRecord(word) && Array.isArray(word.syllables))
  );
}

export function parseVoiceNotePronunciationPacket(
  payload: Uint8Array
): VoiceNotePronunciationState | null {
  let value: unknown;
  try {
    value = JSON.parse(new TextDecoder().decode(payload));
  } catch {
    return null;
  }
  if (!value || typeof value !== "object") return null;
  const packet = value as Record<string, unknown>;
  if (
    packet.type !== VOICE_NOTE_PRONUNCIATION_EVENT_TYPE ||
    packet.version !== VOICE_NOTE_PRONUNCIATION_PROTOCOL_VERSION ||
    typeof packet.voiceNoteId !== "string" ||
    !packet.voiceNoteId ||
    !isProvider(packet.provider)
  ) {
    return null;
  }

  const base: VoiceNotePronunciationBase = {
    voiceNoteId: packet.voiceNoteId,
    provider: packet.provider,
    languageCode: typeof packet.languageCode === "string" ? packet.languageCode : undefined,
  };
  if (packet.status === "unavailable") {
    return {
      ...base,
      status: "unavailable",
      error: typeof packet.error === "string" ? packet.error : undefined,
    };
  }
  if (packet.status !== "complete" || !packet.analysis || typeof packet.analysis !== "object") {
    return null;
  }
  const analysis =
    packet.provider === "azure"
      ? isAzureAnalysis(packet.analysis)
        ? packet.analysis
        : null
      : isLingolixAnalysis(packet.analysis)
        ? packet.analysis
        : null;
  if (!analysis) return null;
  return { ...base, status: "complete", analysis };
}
